/* istanbul ignore file*/
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useAcsFetch } from '../hooks/use-acs-fetch'
import {
  StkBucketActivity,
  StkBooster,
  StkToolkitAccount,
  StkBucket,
  StkBucketCategory,
  StkBucketRules,
  StkBucketToBeDeleted,
  StkBucketUpdates,
  StkGrowthChartData,
  StkPotentialChartData,
  TransferDetails,
  StkBoosterActivity,
} from '../types'

export interface IPotentialChartParams {
  year: number | string
  rate: number | string
  recurringAmount?: number | string
}

interface NewBucket {
  bucketCategoryId: number
  bucketNickName: string
}

interface ISavingsToolkitApi {
  createStkBooster: (booster: Partial<StkBooster>) => Promise<StkBooster[]>
  createStkBuckets: (buckets: NewBucket[]) => Promise<StkBucket[]>
  deleteStkBooster: (accountBoosterId: string) => Promise<StkBooster[]>
  deleteStkBucket: (
    bucketToBeDeleted: StkBucketToBeDeleted[],
  ) => Promise<StkBucket[]>
  getStkBoosters: () => Promise<StkBooster[]>
  getStkBoosterActivity: (
    accountBoosterId: string,
  ) => Promise<StkBoosterActivity>
  getStkBucketActivity: (bucketId: string) => Promise<StkBucketActivity>
  getStkBucketCategories: () => Promise<StkBucketCategory[]>
  getStkBucketRules: () => Promise<StkBucketRules | null>
  getStkBuckets: () => Promise<StkBucket[]>
  getStkGrowthChart: () => Promise<StkGrowthChartData | null>
  getStkPotentialChart: (
    params: IPotentialChartParams,
  ) => Promise<StkPotentialChartData | null>
  getStkToolkitAccounts: (boosterType: string) => Promise<StkToolkitAccount[]>
  redistributeStkBuckets: (
    redistributedBuckets: Partial<StkBucket>[],
  ) => Promise<StkBucket[]>
  transferStkBuckets: (transferDetails: TransferDetails) => Promise<StkBucket[]>
  updateStkBooster: (booster: Partial<StkBooster>) => Promise<StkBooster[]>
  updateStkBucketDetails: (
    bucketUpdates: StkBucketUpdates[],
  ) => Promise<StkBucket[]>
  updateStkBucketRules: (
    rulesUpdate: Partial<StkBucketRules>,
  ) => Promise<StkBucketRules>
  updateStkToolkitAccounts: (
    toolkitAccount: Partial<StkToolkitAccount>,
  ) => Promise<Partial<StkToolkitAccount> | undefined>
}

export const useSavingsToolkitApi = (): ISavingsToolkitApi => {
  const { id: accountId } = useParams<{ id: string }>()
  const acsFetch = useAcsFetch({
    withCif: true,
    withGuid: true,
    defaultHeaders: {
      ApplicationVersion: '2.9',
      ApplicationID: 'ALLYUSBOLB',
      ApplicationName: 'AOB',
    },
  })

  const createStkBooster = async (
    booster: Partial<StkBooster>,
  ): Promise<StkBooster[]> => {
    const response = await acsFetch<Record<string, StkBooster[]>>(
      `/v2/bank-accounts/boosters/accounts/${accountId}/boosters`,
      {
        method: 'POST',
        body: JSON.stringify({ boosters: booster }),
      },
    )
    if (response.data) {
      return response.data.boosters
    }
    throw new Error('No boosters payload in response')
  }

  const createStkBuckets = async (
    buckets: NewBucket[],
  ): Promise<StkBucket[]> => {
    const { data } = await acsFetch<Record<string, StkBucket[]>>(
      `/v1/bank-accounts/buckets/accounts/${accountId}/buckets`,
      { method: 'POST', body: JSON.stringify({ buckets }) },
    )
    if (!data?.buckets) {
      throw new Error('No buckets payload in response')
    }
    return data.buckets
  }

  const deleteStkBooster = async (
    accountBoosterId: string,
  ): Promise<StkBooster[]> => {
    const response = await acsFetch<Record<string, StkBooster[]>>(
      `/v2/bank-accounts/boosters/accounts/${accountId}/boosters`,
      {
        method: 'DELETE',
        body: JSON.stringify({ boosters: [{ accountBoosterId }] }),
      },
    )
    if (response.data) {
      return response.data.boosters
    }
    throw new Error('No boosters payload in response')
  }

  const deleteStkBucket = async (
    bucketToBeDeleted: StkBucketToBeDeleted[],
  ): Promise<StkBucket[]> => {
    const { data } = await acsFetch<Record<string, StkBucket[]>>(
      `/v1/bank-accounts/buckets/accounts/${accountId}/buckets`,
      {
        method: 'DELETE',
        body: JSON.stringify({ buckets: bucketToBeDeleted }),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
    return data?.buckets || []
  }

  const getStkBoosters = async (): Promise<StkBooster[]> => {
    const response = await acsFetch<{ boosters: StkBooster[] }>(
      `/v2/bank-accounts/boosters/accounts/${accountId}/boosters`,
      {
        method: 'GET',
      },
    )
    return response.data.boosters || []
  }

  const getStkBoosterActivity = async (
    accountBoosterId: string,
  ): Promise<StkBoosterActivity> => {
    const response = await acsFetch<{
      boosterActivity: StkBoosterActivity
    }>(
      `/v2/bank-accounts/boosters/accountboosters/${accountBoosterId}/transactions`,
      {
        method: 'GET',
      },
    )
    if (response.data) {
      return response.data.boosterActivity
    }
    throw new Error('Missing payload')
  }

  const getStkBucketActivity = async (
    bucketId: string,
  ): Promise<StkBucketActivity> => {
    // Date range goes from today, back to the first day of the month that was
    // the month two months ago. In other words, date range will always begin on the
    // first of a month, end on today, and be between two and three months total span.
    const today = dayjs().tz('America/New_York')
    const startDate = dayjs()
      .tz('America/New_York')
      .month(today.month() - 2)
      .date(1)
      .format()
    const endDate = today.format()
    const { data } = await acsFetch<{ bucket: StkBucketActivity }>(
      `/v1/bank-accounts/buckets/${bucketId}/transactions?startDate=${startDate}&endDate=${endDate}`,
    )
    if (!data) {
      throw new Error('Missing payload')
    }
    return data.bucket
  }

  const getStkBucketCategories = async (): Promise<StkBucketCategory[]> => {
    const { data } = await acsFetch<Record<string, StkBucketCategory[]>>(
      `/v1/bank-accounts/buckets/categories`,
    )
    return data?.categories || []
  }

  const getStkBucketRules = async (): Promise<StkBucketRules | null> => {
    const { data } = await acsFetch<{
      accountBucketSettings: StkBucketRules
    }>(`/v1/bank-accounts/buckets/accounts/${accountId}/buckets/rules`)
    return data?.accountBucketSettings || null
  }

  const getStkBuckets = async (): Promise<StkBucket[]> => {
    const { data } = await acsFetch<Record<string, StkBucket[]>>(
      `/v1/bank-accounts/buckets/accounts/${accountId}/buckets`,
    )
    return data?.buckets || []
  }

  const getStkGrowthChart = async (): Promise<StkGrowthChartData | null> => {
    const { data } = await acsFetch<{
      growthChart: StkGrowthChartData
    }>(`/v1/bank-accounts/chartdata/accounts/${accountId}/charts/growth`)

    return data?.growthChart || null
  }

  const getStkPotentialChart = async ({
    year,
    rate,
    recurringAmount,
  }: IPotentialChartParams): Promise<StkPotentialChartData | null> => {
    const params: IPotentialChartParams = { year, rate }
    if (recurringAmount) {
      params.recurringAmount = recurringAmount
    }
    const query = new URLSearchParams(params as any).toString()
    const { data } = await acsFetch<{
      potentialChart: StkPotentialChartData
    }>(
      `/v1/bank-accounts/chartdata/accounts/${accountId}/charts/potential?${query}`,
    )

    return data?.potentialChart || null
  }

  const getStkToolkitAccounts = async (
    boosterType: string,
  ): Promise<StkToolkitAccount[]> => {
    const response = await acsFetch<{
      accounts: StkToolkitAccount[]
    }>(
      `/v2/bank-accounts/boosters/toolkit-accounts?boosterType=${boosterType}`,
      {
        method: 'GET',
      },
    )
    return response.data.accounts || []
  }

  const redistributeStkBuckets = async (
    redistributedBuckets: Partial<StkBucket>[],
  ): Promise<StkBucket[]> => {
    const { data } = await acsFetch<Record<string, StkBucket[]>>(
      `/v1/bank-accounts/buckets/accounts/${accountId}/buckets/redistribute`,
      {
        method: 'PATCH',
        body: JSON.stringify({ buckets: redistributedBuckets }),
      },
    )
    if (!data?.buckets) {
      throw Error('No buckets payload in response')
    }
    return data.buckets
  }

  const transferStkBuckets = async (
    transferDetails: TransferDetails,
  ): Promise<StkBucket[]> => {
    const { data } = await acsFetch<Record<string, StkBucket[]>>(
      `/v1/bank-accounts/buckets/accounts/${accountId}/features/buckets/transfer`,
      {
        method: 'PATCH',
        body: JSON.stringify({ ...transferDetails }),
      },
    )
    if (!data?.buckets) {
      throw Error('No buckets payload in response')
    }
    return data.buckets
  }

  const updateStkBooster = async (
    booster: Partial<StkBooster>,
  ): Promise<StkBooster[]> => {
    const response = await acsFetch<Record<string, StkBooster[]>>(
      `/v2/bank-accounts/boosters/accounts/${accountId}/boosters`,
      {
        method: 'PATCH',
        body: JSON.stringify({ boosters: booster }),
      },
    )
    if (response.data) {
      return response.data.boosters
    }
    throw new Error('No boosters payload in response')
  }

  const updateStkBucketDetails = async (
    bucketUpdates: StkBucketUpdates[],
  ): Promise<StkBucket[]> => {
    const { data } = await acsFetch<Record<string, StkBucket[]>>(
      `/v1/bank-accounts/buckets/accounts/${accountId}/buckets`,
      {
        method: 'PATCH',
        body: JSON.stringify({ buckets: bucketUpdates }),
      },
    )
    return data?.buckets || []
  }

  const updateStkBucketRules = async (
    rulesUpdate: Partial<StkBucketRules>,
  ): Promise<StkBucketRules> => {
    const { data } = await acsFetch<{
      accountBucketSettings: StkBucketRules
    }>(`/v1/bank-accounts/buckets/accounts/${accountId}/buckets/rules`, {
      method: 'PATCH',
      body: JSON.stringify({ accountBucketSettings: rulesUpdate }),
    })
    if (!data) {
      throw new Error('Missing response payload')
    }
    return data.accountBucketSettings
  }

  const updateStkToolkitAccounts = async (
    toolkitAccount: Partial<StkToolkitAccount>,
  ): Promise<Partial<StkToolkitAccount> | undefined> => {
    const { data } = await acsFetch<{
      accounts: Partial<StkToolkitAccount>
    }>(`/v2/bank-accounts/boosters/toolkit-accounts`, {
      method: 'PATCH',
      body: JSON.stringify({
        account: {
          stkAccountId: toolkitAccount.stkAccountId,
          externalAccountIdentifier: toolkitAccount.accountId,
          accountNumberPvtEncrypt: toolkitAccount.accountNumberPvtEncrypt,
          routingNumber: toolkitAccount.routingNumber,
        },
      }),
    })
    return data.accounts
  }

  return {
    createStkBooster,
    createStkBuckets,
    deleteStkBooster,
    deleteStkBucket,
    getStkBoosters,
    getStkBoosterActivity,
    getStkBucketActivity,
    getStkBucketCategories,
    getStkBucketRules,
    getStkBuckets,
    getStkGrowthChart,
    getStkPotentialChart,
    getStkToolkitAccounts,
    redistributeStkBuckets,
    transferStkBuckets,
    updateStkBooster,
    updateStkBucketDetails,
    updateStkBucketRules,
    updateStkToolkitAccounts,
  }
}
